// The companion speed dial of an open app in cortex's Apps page: the ONE dial component of
// @cortex/app-shell (a project's standalone app draws the same), wired to cortex's api and pages. With an
// app open, the chat launcher BECOMES this dial. WHICH companions is the platform's decision, never this
// file's: the api answers from the app's declaration (its `companions` list, or the defaults) minus the
// add-ons the organization has off, in dial order (@cortex/structure-check companionsFor, GET
// …/apps/:app/companions).
import { CompanionDial as SharedDial, type DialHost } from '@cortex/app-shell';
import * as React from 'react';
import { APP_REACH_HEADER, api } from '../platform/index.ts';
import { BUILT_PAGES } from '../../app/routes.ts';
import { projectPath } from '../platform/index.ts';

/** The dial's host for a preview: the app's companions and pages on cortex's project-branch addresses. */
const hostOf = (branch: string): DialHost => ({
  companions: async (slug, app) => {
    // Named by the app: a person who reaches the project only through it (an app user) gets its chat.
    const { data } = await api.GET('/api/structure/projects/{slug}/branches/{branch}/apps/{app}/companions', {
      params: { path: { slug, branch, app } },
      headers: { [APP_REACH_HEADER]: app },
    });
    // A project's app offers only the companions of the SDKs it pins (always all inside cortex).
    // The app draws its own chat (chat_ui 'app', the headless chat): the dial offers no Chat.
    // The app's frame places the launcher (spec.dial 'app'): nothing floats over the app.
    return {
      companions: (data?.companions ?? []).filter((c) => !(c.key === 'chat' && data?.chatUi === 'app')),
      dial: data?.dial === 'app' ? ('app' as const) : ('host' as const),
    };
  },
  pages: BUILT_PAGES as unknown as DialHost['pages'],
  pagePath: (page, slug) => projectPath(page, slug, branch),
});

export function CompanionDial({
  branch,
  ...props
}: {
  slug: string;
  branch: string;
  app: string;
  hidden?: boolean;
}) {
  // The shared dial asks cortex's host of this preview's branch.
  const host = React.useMemo(() => hostOf(branch), [branch]);
  return <SharedDial host={host} {...props} />;
}
